import { useEffect, useState } from "react";
import { Card, Table, Input, Button, Tag, Dropdown, Menu, message } from "antd";
import { DownOutlined, SearchOutlined } from "@ant-design/icons";
import { Link, useLocation } from "react-router-dom";
import {
  activateWorkspaceApi,
  listWorkspacesApi,
  suspendWorkspaceApi,
} from "../api/admin.workspaces.api";
import { meApi } from "../api/auth.api";
import WorkspaceDeleteModal from "../components/workspaces/WorkspaceDeleteModal";

const SUBSCRIPTION_COLOR = {
  active: "green",
  canceled: "red",
  expired: "red",
  past_due: "orange",
  disputed: "volcano",
  pending: "gold",
  pending_payment: "gold",
};

const ONBOARDING_COLOR = {
  completed: "green",
  in_progress: "blue",
  not_started: "default",
};

function statusLabel(value) {
  if (!value) return "";
  return String(value)
    .replace(/_/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

function StatusTag({ value, colors }) {
  if (!value) return "—";
  return <Tag color={colors[value] || "default"}>{statusLabel(value)}</Tag>;
}

function Workspaces() {
  const location = useLocation();
  const query = new URLSearchParams(location.search);
  const lifecycleStatus = query.get("lifecycle_status") || undefined;
  const onboardingStatus = query.get("onboarding_status") || undefined;
  const ga4Skipped = query.get("ga4_skipped");
  const staleDays = query.get("onboarding_stale_days");
  const createdSinceDays = query.get("created_since_days");
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 20, total: 0 });
  const [search, setSearch] = useState("");
  const [currentUser, setCurrentUser] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const role = currentUser?.user?.role;
  const isSuperAdmin = role === "super_admin";
  const canManageLifecycle = role === "admin" || role === "super_admin";

  const isSuspended = (status) => status === "suspended" || status === "archived";

  const changeLifecycle = async (row, next) => {
    try {
      const res =
        next === "suspend"
          ? await suspendWorkspaceApi(row.workspace_id)
          : await activateWorkspaceApi(row.workspace_id);
      if (res.data.status === "success") {
        message.success(res.data.message || "Updated");
        fetchData(pagination.current);
      } else {
        message.error(res.data.message || "Failed to update workspace");
      }
    } catch (e) {
      message.error("Failed to update workspace");
    }
  };

  const fetchData = async (page = 1, searchText = search) => {
    try {
      setLoading(true);
      const res = await listWorkspacesApi({
        page,
        page_size: pagination.pageSize,
        search: searchText || undefined,
        lifecycle_status: lifecycleStatus,
        onboarding_status: onboardingStatus,
        ga4_skipped: ga4Skipped === null ? undefined : ga4Skipped === "true",
        onboarding_stale_days: staleDays ? Number(staleDays) : undefined,
        created_since_days: createdSinceDays ? Number(createdSinceDays) : undefined,
      });
      if (res.data.status === "success") {
        const data = res.data.data || {};
        setRows((data.workspaces || []).map((w) => ({ ...w, key: w.workspace_id })));
        setPagination((p) => ({
          ...p,
          current: data.page || page,
          total: data.total || 0,
        }));
      } else {
        message.error(res.data.message || "Failed to load workspaces");
      }
    } catch (e) {
      message.error("Error loading workspaces");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  useEffect(() => {
    meApi()
      .then((res) => {
        if (res.data.status === "success") setCurrentUser(res.data.data);
      })
      .catch(() => {});
  }, []);

  const columns = [
    {
      title: "Workspace",
      dataIndex: "name",
      width: 180,
      ellipsis: true,
      render: (name, r) => (
        <Link to={`/workspaces/${r.workspace_id}`}>{name}</Link>
      ),
    },
    { title: "Owner", dataIndex: "owner_email", width: 200, ellipsis: true },
    { title: "Plan", dataIndex: "plan_name", width: 140, ellipsis: true, render: (v) => v || "—" },
    {
      title: "Status",
      dataIndex: "subscription_status",
      width: 150,
      render: (status) => <StatusTag value={status} colors={SUBSCRIPTION_COLOR} />,
    },
    {
      title: "Onboarding",
      dataIndex: "onboarding_status",
      width: 130,
      render: (status) => <StatusTag value={status} colors={ONBOARDING_COLOR} />,
    },
    {
      title: "Seats",
      key: "seats",
      width: 80,
      render: (_, r) =>
        r.seats_limit != null
          ? `${r.seats_used}/${r.seats_limit}`
          : r.seats_used,
    },
    { title: "Properties", dataIndex: "properties_active", width: 100 },
    {
      title: "MRR",
      dataIndex: "mrr",
      width: 120,
      render: (v, row) =>
        typeof v === "number" && v > 0 && row.mrr_currency
          ? `${row.mrr_currency} ${Number(v).toFixed(2)}`
          : "—",
    },
    {
      title: "Action",
      key: "actions",
      width: 120,
      render: (_, row) => {
        if (!canManageLifecycle && !isSuperAdmin) {
          return isSuspended(row.lifecycle_status) ? (
            <Tag color="red">Suspended</Tag>
          ) : (
            <Tag color="green">{statusLabel(row.lifecycle_status || "active")}</Tag>
          );
        }
        const suspended = isSuspended(row.lifecycle_status);
        return (
          <Dropdown
            trigger={["click"]}
            overlay={
              <Menu
                onClick={({ key }) => {
                  if (key === "delete") setDeleteTarget(row);
                  else changeLifecycle(row, key);
                }}
              >
                {canManageLifecycle ? (
                  <Menu.Item key={suspended ? "activate" : "suspend"}>
                    {suspended ? "Activate" : "Suspend"}
                  </Menu.Item>
                ) : null}
                {isSuperAdmin ? (
                  <Menu.Item key="delete">
                    <span style={{ color: "#ff4d4f" }}>Delete</span>
                  </Menu.Item>
                ) : null}
              </Menu>
            }
          >
            <Button size="small">
              Actions <DownOutlined />
            </Button>
          </Dropdown>
        );
      },
    },
  ];

  return (
    <div className="layout-content workspaces-page">
      <style>{`
        .workspaces-page .ant-card-head-wrapper {
          min-height: 0;
        }
        .workspaces-page .ant-table-tbody > tr > td,
        .workspaces-page .ant-table-thead > tr > th {
          padding: 8px 12px;
        }
        .workspaces-page .ant-table-thead > tr > th::before,
        .workspaces-page .ant-table-cell-fix-right-first::after {
          display: none;
        }
      `}</style>
      <Card
        title="Workspaces"
        extra={
          <Input
            allowClear
            prefix={<SearchOutlined />}
            placeholder="Search name"
            value={search}
            onChange={(event) => {
              const value = event.target.value;
              setSearch(value);
              if (!value) fetchData(1, "");
            }}
            onPressEnter={() => fetchData(1, search)}
            style={{ width: 220 }}
          />
        }
      >
        <Table
          size="small"
          loading={loading}
          columns={columns}
          dataSource={rows}
          scroll={{ x: "max-content" }}
          pagination={{
            ...pagination,
            onChange: (page) => fetchData(page),
          }}
        />
      </Card>
      <WorkspaceDeleteModal
        open={Boolean(deleteTarget)}
        workspaceId={deleteTarget?.workspace_id}
        onCancel={() => setDeleteTarget(null)}
        onDeleted={() => {
          setDeleteTarget(null);
          fetchData(pagination.current);
        }}
      />
    </div>
  );
}

export default Workspaces;
